/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { combineReducers } from 'redux';
import reduceReducers from 'reduce-reducers';
import { dialogReducer } from 'redux-dialog';
import { reducer as formReducer } from 'redux-form';

import { monitorReducer } from './Monitor/reducers/monitorReducer';
import ServicesPage from './Services/reducers/index';
import { tenantReducer } from './Common/reducers/tenantReducer';
import { applicationErrorsReducer } from './Common/reducers/applicationErrorsReducer';
import { userReducer } from './Common/reducers/userReducer';
import { monitoringReducer } from './Dashboard/reducers/monitoringReducer';
import { domainReducer } from './Setup/reducers/domainReducer';
import { manageReducer } from './Common/reducers/manageReducer';
import { serverReducer } from './Common/reducers/serverReducer';
import {
  monitorDBSchemasReducer,
  factoryTalkSourceTablesReducer,
  factoryTalkSourceTablesTagsReducer,
  factoryTalkServiceConfigurationsReducer,
  validateFactoryTalkDatabaseConnectionReducer,
  serviceConfigFileReducer,
  serviceConfigFileNamesReducer,
  deleteServiceConfigFilesReducer,
  uploadServiceConfigFilesReducer,
} from './Services/reducers/FactoryTalkReducers';
import { assetResourcesReducer } from './Setup/reducers/assetResourcesReducer';
import { locationResourcesReducer } from './Setup/reducers/locationResourcesReducer';
import { hierarchiesReducer } from './Setup/reducers/hierarchiesReducer';

import { usersReducer } from 'usersReducer';
import { OtherServicesReducer } from 'otherServicesReducer';
import { rolesReducer } from 'rolesReducer';
import { entityTypesReducer, entityTypeDetailsReducer } from 'entityTypesReducer';
import { archivedHierarchyItemsReducer } from 'archivesReducer';
import { globalReducer } from 'globalReducer';
import { analyticsEngineReducer } from 'analyticsEngineReducer';
import { constantReducer, globalConstantReducer } from 'constantReducer';
import {
  kpisReducer,
  hierarchyItemKPIsReducer,
  kpiLogReducer,
  kpiFunctionsReducer,
} from 'kpisReducer';
import {
  dataItemDetailsReducer,
  dataItemGraphReducer,
  dataItemsReducer,
  hierarchyDataItemsReducer,
  dataItemRecentDataReducer,
} from 'dataItemsReducer';
import { availableAggregatesReducer } from 'availableAggregatesReducer';
import { catalogFunctionsReducer } from 'src/Setup/reducers/catalogFunctionsReducer';
import { granularitiesReducer } from 'granularitiesReducer';
import {
  summariesReducer,
  summaryReducer,
  validDataItemsForSummaryReducer,
} from 'summariesReducer';
import { dashboardPinsReducer } from 'dashboardPinsReducer';
import { connectReducer } from 'connectReducer';
import { devicesReducer } from 'devicesReducer';
import { typesReducer } from 'typesReducer';
import { interfacesReducer } from 'interfacesReducer';
import { logicalInterfaceCatalogReducer } from 'logicalInterfaceCatalogReducer';

// I have a global reducer that operates on all the redux data
export const rootReducer = reduceReducers(
  {},
  combineReducers({
    entityTypes: entityTypesReducer,
    archivedHierarchyItems: archivedHierarchyItemsReducer,
    analyticsEngine: analyticsEngineReducer,
    entityTypeDetails: entityTypeDetailsReducer,
    dataItems: dataItemsReducer,
    hierarchyDataItems: hierarchyDataItemsReducer,
    validDataItemsForSummary: validDataItemsForSummaryReducer,
    dataItemDetails: dataItemDetailsReducer,
    kpis: kpisReducer,
    hierarchyLevelKPIs: hierarchyItemKPIsReducer,
    kpiFunctions: kpiFunctionsReducer,
    catalogFunctions: catalogFunctionsReducer,
    dataItemGraphs: dataItemGraphReducer,
    selectedKPILog: kpiLogReducer,
    availableAggregates: availableAggregatesReducer,
    serverConfiguration: serverReducer,
    tenant: tenantReducer,
    granularities: granularitiesReducer,
    summaries: summariesReducer,
    summary: summaryReducer,
    constants: constantReducer,
    globalContants: globalConstantReducer,
    ServicesPage,
    users: usersReducer,
    domains: domainReducer,
    roles: rolesReducer,
    platformConnectionInfo: connectReducer,
    devices: devicesReducer,
    deviceTypes: typesReducer,
    interfaces: interfacesReducer,
    dataItemRecentData: dataItemRecentDataReducer,
    monitoring: monitoringReducer,
    dashboardPins: dashboardPinsReducer,
    hierarchies: hierarchiesReducer,
    // Usage reducers
    otherServices: OtherServicesReducer,
    monitorDBSchemas: monitorDBSchemasReducer,
    factoryTalkSourceTables: factoryTalkSourceTablesReducer,
    factoryTalkSourceTablesTags: factoryTalkSourceTablesTagsReducer,
    factoryTalkServiceConfigurations: factoryTalkServiceConfigurationsReducer,
    validateFactoryTalkDatabaseConnection: validateFactoryTalkDatabaseConnectionReducer,
    serviceConfigFile: serviceConfigFileReducer,
    serviceConfigFileNames: serviceConfigFileNamesReducer,
    deleteServiceConfigFiles: deleteServiceConfigFilesReducer,
    uploadServiceConfigFiles: uploadServiceConfigFilesReducer,
    // Truly Common Reducers
    applicationErrors: applicationErrorsReducer,
    currentUser: userReducer,
    form: formReducer,
    dialogReducer,
    logicalInterfaceCatalog: logicalInterfaceCatalogReducer,
    monitor: monitorReducer,
    assetResources: assetResourcesReducer,
    locationResources: locationResourcesReducer,
    manageAvailability: manageReducer,
  }),
  globalReducer
);

export default rootReducer;
